import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { toast } from 'sonner';
import apiService from '../services/api.service';
import { getOrCreateSessionId, refreshSessionExpiry, getSessionInfo } from '../utils/sessionManager';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

const cartReducer = (state, action) => {
  console.log('Cart reducer - Action:', action.type, 'Payload:', action.payload);
  switch (action.type) {
    case 'SET_CART':
      const newState = {
        ...state,
        items: action.payload.items || [],
        total: action.payload.totalAmount || action.payload.total_amount || 0,
        loading: false
      };
      console.log('Cart reducer - New state after SET_CART:', newState);
      return newState;
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload
      };
    case 'ADD_ITEM':
      return {
        ...state,
        items: [...state.items, action.payload],
        total: state.total + (parseFloat(action.payload.price.replace('₹', '').replace('$', '')) * action.payload.quantity)
      };
    case 'REMOVE_ITEM':
      const filteredItems = state.items.filter(item => item.productId !== action.payload);
      const newTotal = filteredItems.reduce((sum, item) => 
        sum + (parseFloat(item.price.replace('₹', '').replace('$', '')) * item.quantity), 0
      );
      return {
        ...state,
        items: filteredItems,
        total: newTotal
      };
    case 'CLEAR_CART':
      return {
        ...state,
        items: [],
        total: 0
      };
    case 'UPDATE_QUANTITY':
      const updatedItems = state.items.map(item => {
        if (item.productId === action.payload.productId) {
          return { ...item, quantity: action.payload.quantity };
        }
        return item;
      });
      const updatedTotal = updatedItems.reduce((sum, item) => 
        sum + (parseFloat(item.price.replace('₹', '').replace('$', '')) * item.quantity), 0
      );
      return {
        ...state,
        items: updatedItems,
        total: updatedTotal
      };
    case 'SET_CART_OPEN':
      return {
        ...state,
        isOpen: action.payload
      };
    default:
      return state;
  }
};

const initialState = {
  items: [],
  total: 0,
  loading: false,
  isOpen: false
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const [sessionId, setSessionId] = React.useState(null);

  // Initialize session on component mount
  useEffect(() => {
    const id = getOrCreateSessionId();
    setSessionId(id);
    console.log('[Cart] Initialized with session:', id);
    console.log('[Cart] Session info:', getSessionInfo());
  }, []);

  // Load cart from backend on component mount
  useEffect(() => {
    if (sessionId) {
      loadCart();
    }
  }, [sessionId]);

  const loadCart = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await apiService.cart.getCart(sessionId);
      console.log('Cart loaded from API:', response.data);
      
      // Only update if response has valid items array or cart data
      if (response.data) {
        const hasItems = response.data.items && response.data.items.length > 0;
        const hasValidTotal = response.data.totalAmount !== undefined || response.data.total_amount !== undefined;
        
        console.log(`Cart data check - hasItems: ${hasItems}, hasValidTotal: ${hasValidTotal}, items count: ${response.data.items?.length || 0}`);
        
        // Update state with server data
        dispatch({ type: 'SET_CART', payload: response.data });
      }
      dispatch({ type: 'SET_LOADING', payload: false });
    } catch (error) {
      console.error('Failed to load cart:', error);
      // Don't clear the cart on error - keep optimistic adds
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const addToCart = async (product) => {
    try {
      console.log('Adding to cart:', product);
      
      // Refresh session expiry to keep session alive
      refreshSessionExpiry();
      
      const cartItem = {
        productId: product.id.toString(),
        name: product.name,
        price: product.price,
        quantity: 1,
        image: product.image
      };

      console.log('Cart item prepared:', cartItem);
      
      // Optimistically add to local state immediately
      dispatch({ type: 'ADD_ITEM', payload: cartItem });
      console.log('Added to local state optimistically');
      
      // Send to backend asynchronously (don't wait)
      if (sessionId) {
        apiService.cart.addItem(cartItem, sessionId)
          .then(response => {
            console.log('Backend confirmed add:', response.data);
            // Sync with server after a delay to get authoritative state
            setTimeout(() => {
              loadCart();
            }, 500);
          })
          .catch(error => {
            console.error('Failed to add to backend:', error);
            // Keep the optimistic add - user can still checkout
          });
      }
      
      // Show toast with close button and action button for dismiss
      const toastId = toast.success(`${product.name} added to cart!`, {
        duration: 5000,
        action: {
          label: '✕',
          onClick: () => toast.dismiss(toastId),
        },
      });
    } catch (error) {
      console.error('Failed to add item to cart:', error);
      toast.error('Failed to add item to cart');
    }
  };

  const removeFromCart = async (productId) => {
    try {
      refreshSessionExpiry();
      if (sessionId) {
        await apiService.cart.removeItem(productId, sessionId);
      }
      dispatch({ type: 'REMOVE_ITEM', payload: productId });
      
      const removeToastId = toast.success('Item removed from cart', {
        duration: 5000,
        action: {
          label: '✕',
          onClick: () => toast.dismiss(removeToastId),
        },
      });
    } catch (error) {
      console.error('Failed to remove item from cart:', error);
      toast.error('Failed to remove item from cart');
    }
  };

  const updateQuantity = async (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }

    try {
      refreshSessionExpiry();
      dispatch({ type: 'UPDATE_QUANTITY', payload: { productId, quantity } });
      // Call backend to update quantity
      if (sessionId) {
        await apiService.cart.updateItem(productId, quantity, sessionId);
      }
    } catch (error) {
      console.error('Failed to update quantity:', error);
      toast.error('Failed to update quantity');
    }
  };

  const clearCart = async () => {
    try {
      refreshSessionExpiry();
      
      // Call backend DELETE /api/cart/{sessionId} to permanently delete cart
      if (sessionId) {
        console.log('[Cart] Calling DELETE /api/cart to clear cart permanently');
        await apiService.cart.clearCart(sessionId);
      }
      
      // Clear local state
      dispatch({ type: 'CLEAR_CART' });
      
      const clearToastId = toast.success('Cart cleared', {
        duration: 5000,
        action: {
          label: '✕',
          onClick: () => toast.dismiss(clearToastId),
        },
      });
    } catch (error) {
      console.error('Failed to clear cart:', error);
      toast.error('Failed to clear cart');
    }
  };

  const getCartItemsCount = () => {
    return state.items.reduce((total, item) => total + item.quantity, 0);
  };

  const openCart = () => {
    dispatch({ type: 'SET_CART_OPEN', payload: true });
  };

  const closeCart = () => {
    dispatch({ type: 'SET_CART_OPEN', payload: false });
  };

  const value = {
    ...state,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartItemsCount,
    openCart,
    closeCart,
    sessionId
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
